/**
 * Financial Data Normalizer
 * 
 * This utility handles the conversion between different financial data formats
 * used throughout the application. It provides a single source of truth for
 * data structure conversion.
 * 
 * DATA FORMATS:
 * 
 * 1. plFiles format (from file uploads):
 *    [{year: 2020, data: [{month: 1, revenue: 50000, ...}, ...]}, ...]
 * 
 * 2. data_YYYY format (from bulk upload/CSV):
 *    {data_2020: [{month: 1, revenue: 50000, ...}, ...], data_2021: [...]}
 * 
 * 3. aiExtractedData format (from AI extraction):
 *    {revenue: [{month: 'Jan', amount: 50000, ...}], expenses: {...}, payroll: [...]}
 * 
 * 4. Normalized format (unified):
 *    {
 *      years: {
 *        2020: [{month: 1, revenue: 50000, expenses: 40000, ...}, ...],
 *        2021: [...]
 *      },
 *      metadata: {
 *        availableYears: [2020, 2021],
 *        hasMultipleYears: true,
 *        source: 'plFiles' | 'data_YYYY' | 'aiExtracted'
 *      }
 *    }
 */

import { resolveBucketYear } from "./resolveCoverageYear";

export interface MonthlyData {
  month: number;
  revenue: number;
  expenses?: number;
  grossProfit?: number;
  [key: string]: any;
}

export interface NormalizedFinancialData {
  years: { [year: number]: MonthlyData[] };
  metadata: {
    availableYears: number[];
    hasMultipleYears: boolean;
    source: 'plFiles' | 'data_YYYY' | 'aiExtracted' | 'unknown';
  };
}

/**
 * Normalize any financial data format into a unified structure
 */
export function normalizeFinancialData(
  data: any,
  plFiles?: any[]
): NormalizedFinancialData {
  const result: NormalizedFinancialData = {
    years: {},
    metadata: {
      availableYears: [],
      hasMultipleYears: false,
      source: 'unknown'
    }
  };

  // Priority 1: plFiles format (most reliable from file uploads)
  if (plFiles && plFiles.length > 0) {
    console.log('📊 Normalizer: Processing plFiles format');
    result.metadata.source = 'plFiles';
    
    for (const file of plFiles) {
      const year = file.year;
      if (!year) continue;
      
      const monthlyData: MonthlyData[] = [];
      const fileData = file.data || [];
      
      for (const item of fileData) {
        const month = item.month || (monthlyData.length + 1);
        monthlyData.push({
          month: typeof month === 'number' ? month : parseMonth(month),
          revenue: parseNumber(item.revenue),
          expenses: parseNumber(item.expenses || item.total_expenses),
          grossProfit: parseNumber(item.gross_profit || item.grossProfit)
        });
      }
      
      // Ensure 12 months
      while (monthlyData.length < 12) {
        monthlyData.push({
          month: monthlyData.length + 1,
          revenue: 0,
          expenses: 0
        });
      }
      
      result.years[year] = monthlyData.slice(0, 12);
    }
  }
  
  // Priority 2: data_YYYY format (from bulk upload/CSV)
  if (Object.keys(result.years).length === 0 && data) {
    const yearKeys = Object.keys(data).filter(k => k.startsWith('data_'));
    
    if (yearKeys.length > 0) {
      console.log('📊 Normalizer: Processing data_YYYY format');
      result.metadata.source = 'data_YYYY';
      
      for (const key of yearKeys) {
        const year = parseInt(key.replace('data_', ''));
        if (isNaN(year)) continue;
        
        const yearData = data[key] || [];
        const monthlyData: MonthlyData[] = [];
        
        for (let i = 0; i < yearData.length; i++) {
          const item = yearData[i];
          monthlyData.push({
            month: item.month || (i + 1),
            revenue: parseNumber(item.revenue),
            expenses: parseNumber(item.expenses || item.total_expenses),
            grossProfit: parseNumber(item.gross_profit || item.grossProfit)
          });
        }
        
        // Ensure 12 months
        while (monthlyData.length < 12) {
          monthlyData.push({
            month: monthlyData.length + 1,
            revenue: 0,
            expenses: 0
          });
        }
        
        result.years[year] = monthlyData.slice(0, 12);
      }
    }
  }
  
  // Priority 3: aiExtracted format (revenue/expenses arrays)
  if (Object.keys(result.years).length === 0 && data) {
    if (data.revenue && Array.isArray(data.revenue)) {
      console.log('📊 Normalizer: Processing aiExtracted format');
      result.metadata.source = 'aiExtracted';
      
      // Group by year.  Use resolveBucketYear — NEVER silently bucket under
      // today's year.  Items with no year AND no claim-level coverage year
      // get dropped with a warning (prevents phantom 2026-04 rows).
      const yearGroups: { [year: number]: MonthlyData[] } = {};
      let droppedRevenue = 0;

      for (const item of data.revenue) {
        const year = resolveBucketYear(item, data);
        if (year === null) {
          droppedRevenue += 1;
          continue;
        }
        const month = parseMonth(item.month);

        if (!yearGroups[year]) {
          yearGroups[year] = [];
        }

        yearGroups[year].push({
          month,
          revenue: parseNumber(item.amount || item.revenue),
          expenses: 0
        });
      }

      if (droppedRevenue > 0) {
        console.warn(
          `📊 Normalizer: Dropped ${droppedRevenue} revenue rows with unresolvable year (no item.year and no claim coverage year)`
        );
      }

      // Add expenses if available
      if (data.expenses) {
        let droppedExpenses = 0;
        // Handle expenses object or array
        for (const [, items] of Object.entries(data.expenses)) {
          if (Array.isArray(items)) {
            for (const item of items as any[]) {
              const year = resolveBucketYear(item, data);
              if (year === null) {
                droppedExpenses += 1;
                continue;
              }
              const month = parseMonth(item.month);

              if (yearGroups[year]) {
                const monthData = yearGroups[year].find(m => m.month === month);
                if (monthData) {
                  monthData.expenses = (monthData.expenses || 0) + parseNumber(item.amount);
                }
              }
            }
          }
        }
        if (droppedExpenses > 0) {
          console.warn(
            `📊 Normalizer: Dropped ${droppedExpenses} expense rows with unresolvable year`
          );
        }
      }
      
      result.years = yearGroups;
    }
  }
  
  // Update metadata
  result.metadata.availableYears = Object.keys(result.years)
    .map(y => parseInt(y))
    .sort((a, b) => a - b);
  result.metadata.hasMultipleYears = result.metadata.availableYears.length >= 2;
  
  console.log('📊 Normalizer result:', {
    years: result.metadata.availableYears,
    source: result.metadata.source,
    hasMultipleYears: result.metadata.hasMultipleYears
  });
  
  return result;
}

/**
 * Convert normalized data to data_YYYY format (for backend API)
 */
export function toDataYYYYFormat(normalized: NormalizedFinancialData): { [key: string]: MonthlyData[] } {
  const result: { [key: string]: MonthlyData[] } = {};
  
  for (const [year, data] of Object.entries(normalized.years)) {
    result[`data_${year}`] = data;
  }
  
  return result;
}

/**
 * Convert normalized data to plFiles format
 */
export function toPlFilesFormat(normalized: NormalizedFinancialData): any[] {
  return Object.entries(normalized.years).map(([year, data]) => ({
    year: parseInt(year),
    data
  }));
}

/**
 * Check if we have enough data for loss period detection
 */
export function hasEnoughDataForDetection(
  data: any,
  plFiles?: any[]
): boolean {
  const normalized = normalizeFinancialData(data, plFiles);
  return normalized.metadata.hasMultipleYears;
}

/**
 * Get data ready for loss period detection API
 */
export function getDetectionPayload(
  data: any,
  plFiles?: any[]
): { plFiles?: any[]; financial_data?: any } | null {
  const normalized = normalizeFinancialData(data, plFiles);
  
  if (!normalized.metadata.hasMultipleYears) {
    console.log('⚠️ Not enough years for detection:', normalized.metadata.availableYears);
    return null;
  }
  
  // Return in the format the backend expects
  if (normalized.metadata.source === 'plFiles') {
    return { plFiles: toPlFilesFormat(normalized) };
  } else {
    return { financial_data: toDataYYYYFormat(normalized) };
  }
}

// Helper functions

function parseNumber(value: any): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[$,]/g, '').trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

function parseMonth(value: any): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const monthMap: { [key: string]: number } = {
      'jan': 1, 'january': 1,
      'feb': 2, 'february': 2,
      'mar': 3, 'march': 3,
      'apr': 4, 'april': 4,
      'may': 5,
      'jun': 6, 'june': 6,
      'jul': 7, 'july': 7,
      'aug': 8, 'august': 8,
      'sep': 9, 'september': 9,
      'oct': 10, 'october': 10,
      'nov': 11, 'november': 11,
      'dec': 12, 'december': 12
    };
    const lower = value.toLowerCase();
    if (monthMap[lower]) return monthMap[lower];
    
    // Try parsing as number
    const parsed = parseInt(value);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 12) return parsed;
  }
  return 1;
}
